"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useCallback, useSyncExternalStore } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Bug, ChevronRight } from "lucide-react";
import { TenantSwitcher } from "@/components/tenant-switcher";
import { usePermissions } from "@/lib/hooks/use-permissions";
import { visibleForRole } from "@/lib/permissions";
import {
  FOOTER_NAV,
  MORE_NAV,
  MORE_OPEN_KEY,
  PRIMARY_NAV,
  isInMore,
  isNavItemActive,
  type NavItem,
} from "@/lib/navigation";

const MORE_EVENT = "rii:sidebar-more-changed";

function readMoreOpen(): boolean {
  try {
    return localStorage.getItem(MORE_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

/** Whether the user opened "More", remembered in this browser. */
function useMoreOpen(): [boolean, () => void] {
  const open = useSyncExternalStore(
    (onChange) => {
      window.addEventListener("storage", onChange);
      window.addEventListener(MORE_EVENT, onChange);
      return () => {
        window.removeEventListener("storage", onChange);
        window.removeEventListener(MORE_EVENT, onChange);
      };
    },
    readMoreOpen,
    () => false,
  );
  const toggle = useCallback(() => {
    try {
      localStorage.setItem(MORE_OPEN_KEY, readMoreOpen() ? "0" : "1");
    } catch {
      /* not persisted */
    }
    window.dispatchEvent(new Event(MORE_EVENT));
  }, []);
  return [open, toggle];
}

function NavLinks({ items, pathname }: { items: NavItem[]; pathname: string | null }) {
  return (
    <div className="space-y-1">
      {items.map((item) => {
        const isActive = isNavItemActive(pathname, item.href);
        return (
          <Link key={item.name} href={item.href}>
            <Button
              variant={isActive ? "secondary" : "ghost"}
              className={cn("w-full justify-start", isActive && "bg-secondary")}
            >
              <item.icon className="mr-3 h-5 w-5" />
              {item.name}
            </Button>
          </Link>
        );
      })}
    </div>
  );
}

const debugEnabled =
  process.env.DEBUG === "true" ||
  process.env.DEBUG === "1" ||
  process.env.NEXT_PUBLIC_DEBUG === "true" ||
  process.env.NEXT_PUBLIC_DEBUG === "1";

export function Sidebar() {
  const pathname = usePathname();
  const { role } = usePermissions();
  const [moreChosen, toggleMore] = useMoreOpen();
  // Open by choice, or because the current page lives inside it.
  const moreOpen = moreChosen || isInMore(pathname);

  return (
    <div className="hidden md:flex h-full w-64 flex-col border-r bg-muted/40">
      <div className="flex h-16 items-center justify-center border-b px-6">
        <Link href="/" className="flex items-center">
          <Image
            src="/logos/logo-dark.png"
            alt="rii.link"
            width={120}
            height={32}
            className="dark:hidden"
            priority
          />
          <Image
            src="/logos/logo-light.png"
            alt="rii.link"
            width={120}
            height={32}
            className="hidden dark:block"
            priority
          />
        </Link>
      </div>

      <div className="border-b p-4">
        <TenantSwitcher />
      </div>

      <nav className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
        <NavLinks items={visibleForRole(PRIMARY_NAV, role)} pathname={pathname} />

        <div className="border-t pt-4">
          <Button
            variant="ghost"
            className="w-full justify-between text-muted-foreground"
            aria-expanded={moreOpen}
            aria-controls="sidebar-more"
            onClick={toggleMore}
          >
            More
            <ChevronRight
              className={cn("h-4 w-4 transition-transform", moreOpen && "rotate-90")}
              aria-hidden="true"
            />
          </Button>
          {moreOpen && (
            <div id="sidebar-more" className="mt-1">
              <NavLinks items={visibleForRole(MORE_NAV, role)} pathname={pathname} />
            </div>
          )}
        </div>

        <div className="mt-auto border-t pt-4">
          <NavLinks items={visibleForRole(FOOTER_NAV, role)} pathname={pathname} />
        </div>

        {debugEnabled && (
          <div className="mt-6 border-t pt-4">
            <div className="space-y-1">
              <Link href="/health/sentry">
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start border border-red-500/70 bg-red-50 text-red-900 hover:bg-red-100 dark:bg-red-950 dark:text-red-100 dark:border-red-500",
                    pathname === "/health/sentry" && "ring-2 ring-red-500",
                  )}
                >
                  <Bug className="mr-3 h-5 w-5" />
                  Sentry Health Check
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </div>
  );
}
